import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { stringify } from "qs";
import { useTranslation } from "react-i18next";
import { unwrapResult } from "@reduxjs/toolkit";
import { css } from "@emotion/react";
import { decamelize } from "humps";
import { FlightTravelerBlock } from "main/javascripts/components/flight/FlightTravelerBlock";
import { FlightOriginDestinationDate } from "main/javascripts/components/flight/FlightOriginDestinationDate";
import { FlightTypeBlock } from "main/javascripts/components/flight/form/FlightTypeBlock";
import { ButtonWithLoader } from "main/javascripts/components/button/ButtonWithLoader";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { searchCategories } from "main/javascripts/constants/SearchCategories";
import { Region } from "main/javascripts/types/region";
import {
  FormNamespaces,
  yupDefaultMessages,
} from "main/javascripts/constants/FormConstants";
import { FlightTypes } from "main/javascripts/constants/FlightTypes";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { CabinClassBlock } from "main/javascripts/components/flight/form/CabinClassBlock";
import { FormErrorsBlock } from "main/javascripts/components/form/FormErrorsBlock";
import { debouncedFetchFlightDestinationSuggestions } from "../../../flight/flightSuggestionSlice";
import { fontSize } from "main/javascripts/styles/base/typographyStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { flightDestinationRegionSelector } from "../lpFlightDstRegionSlice";
import {
  convertSearchFlightFormValuesToParams,
  postSearchFlightCache,
} from "../../../flight/searchFlightSlice";
import { flightAvailabilitiesEvent } from "main/javascripts/utils/googleTagManagerUtil";
import { convertServerErrorToForm } from "main/javascripts/utils/FormUtil";
import { PostSearchGAParams } from "main/javascripts/constants/PostConstants";
import { TABI_DOMAIN } from "main/javascripts/constants/Constants";
import { textColor } from "main/javascripts/styles/base/colorStyle";

type SearchFlightFormData = {
  form?: any; // エラー用
  flight_type: string;
  origin: string;
  origin_id: number;
  origin_type: string;
  destination: string;
  destination_id: number;
  destination_type: string;
  departure_date: string;
  return_date: string;
  itineraries: any[];
  traveler: any;
};

yup.setLocale(yupDefaultMessages);
const schema = yup.object().shape({
  origin: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  origin_id: yup
    .number()
    .nullable()
    .transform((_, val) => (val ? Number(val) : null))
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.number().nullable().required(),
    }),
  destination: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  destination_id: yup
    .number()
    .nullable()
    .transform((_, val) => (val ? Number(val) : null))
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.number().nullable().required(),
    }),
  departure_date: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val !== FlightTypes.multiCity,
      then: yup.string().nullable().required(),
    }),
  return_date: yup
    .string()
    .nullable()
    .when("flight_type", {
      is: (val) => val === FlightTypes.roundTrip,
      then: yup.string().nullable().required(),
    }),
  itineraries: yup
    .array()
    .nullable()
    .when("flight_type", {
      is: (val) => val === FlightTypes.multiCity,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            origin: yup.string().required(),
            origin_id: yup.number().required(),
            destination: yup.string().required(),
            destination_id: yup.number().required(),
            departure_date: yup.string().nullable().required(),
          })
        ),
    }),
  traveler: yup.object().shape({
    number_of_children: yup
      .number()
      .nullable()
      .transform((_, val) => (val || val === 0 ? Number(val) : null))
      .required(),
    ages_of_children: yup.array().when("number_of_children", {
      is: (val) => Number(val) > 0,
      then: yup.array().of(
        yup.object().shape({
          age: yup
            .number()
            .nullable()
            .transform((_, val) => (val || val === 0 ? Number(val) : null))
            .required()
            .test(
              "infantsNumber",
              "infantsNumber",
              function (value, context: any) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [_, parent2] = context.from;
                const infantsNumber: number = (
                  parent2?.value?.ages_of_children || []
                ).filter((age: any): boolean => {
                  // 空文字を無視したいためparseIntを使用
                  return parseInt(age.age) <= 1;
                }).length;
                const adultsNumber = Number(
                  parent2?.value?.number_of_adult || 1
                );
                return infantsNumber <= adultsNumber;
              }
            ),
        })
      ),
    }),
  }),
});

const defaultValues = {
  departure_date: null, // undefinedではダメ
  return_date: null, // undefinedではダメ
  traveler: {
    number_of_adult: 2,
    number_of_children: 0,
  },
  access_type: "direct_web",
  flight_type: decamelize("roundTrip"),
  cabin_class: "Y",
  itineraries: [{ departure_date: null }, { departure_date: null }],
};

export const SearchFlight = (): React.ReactElement => {
  const { t } = useTranslation(["component"]);

  const appDispatch = useAppDispatch();

  const flightDestinationRegion = useAppSelector(
    flightDestinationRegionSelector
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: any = {};
  let region: Region.Region = null;
  if (flightDestinationRegion) {
    region = flightDestinationRegion.region;
    const defaultDetionatination =
      flightDestinationRegion.destinationSuggestions[0];
    if (defaultDetionatination) {
      initialValues.destination_id = defaultDetionatination.id;
      initialValues.destination = defaultDetionatination.name;
      initialValues.destination_type = "region";
    }
    // const today: Date = new Date();
    // const departureDate: Date = addMonths(today, 2);
    // const dateFormat = "yyyy/LL/dd";
    // initialValues.departure_date = format(departureDate, dateFormat);
    // initialValues.return_date = format(addDays(departureDate, 5), dateFormat);
  }

  const methods = useForm<SearchFlightFormData>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      ...initialValues,
    },
  });
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    clearErrors("form");
    handleSubmit(onSubmit)(e);
  };

  const onSubmit: (values: any) => void = async (values: any) => {
    setIsSubmitting(true);
    const params: any = convertSearchFlightFormValuesToParams(values);
    return appDispatch(postSearchFlightCache({ params }))
      .then(unwrapResult)
      .then((result) => {
        const cacheId = result.data.cacheId;
        flightAvailabilitiesEvent(cacheId, params);
        const gaParams = stringify(PostSearchGAParams, {
          arrayFormat: "indices",
        });
        window.location.href = `${TABI_DOMAIN}/flights?${stringify(params, {
          arrayFormat: "indices",
        })}&cache_id=${cacheId}&${gaParams}`;
      })
      .catch((errorResponse) => {
        setIsSubmitting(false);
        if (errorResponse.errors) {
          // エラー形式を合わせるために変換している
          const errors = Object.keys(errorResponse.errors).map((key) => {
            return { [key]: errorResponse.errors[key] };
          });
          convertServerErrorToForm({ fieldMessages: errors }, setError);
        }
      });
  };

  const fetchFlightDestinationSuggestionsAction = (
    data: any,
    type: string,
    index?: number
  ) => {
    const { text, ...rest } = data;
    appDispatch(
      debouncedFetchFlightDestinationSuggestions({
        params: { text: text, ...rest },
        type,
        index,
      })
    );
  };

  return (
    <div css={blockStyle}>
      <h1 css={titleStyle}>
        {t("lp.searchFlightByDestinationTitle", {
          destination: region?.name || "",
        })}
      </h1>
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmit}>
          <div css={flightTypeBlockStyle}>
            <FlightTypeBlock
              labelStyleKey="labelDark"
              borderColorStyleKey="secondary"
            />
          </div>
          <FlightOriginDestinationDate
            destinationSuggestOptions={{}}
            defaultDestinations={flightDestinationRegion.destinationSuggestions}
            fetchFlightOriginDestinationSuggestions={
              fetchFlightDestinationSuggestionsAction
            }
            useDatePicker={true}
            searchType={searchCategories.flight}
            labelColorStyleKey="default"
            borderColorStyleKey="secondary"
          />
          <div css={flightClassBlockStyle}>
            <CabinClassBlock
              labelColorStyleKey="default"
              borderColorStyleKey="secondary"
            />
          </div>
          <FlightTravelerBlock
            labelColorStyleKey="default"
            borderColorStyleKey="secondary"
          />
          {errors && (
            <div>
              <FormErrorsBlock
                namespace={FormNamespaces.flight}
                errors={errors}
                hideValidationErrors={true}
              />
            </div>
          )}
          <div css={submitBlockStyle}>
            <ButtonWithLoader
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              検 索
            </ButtonWithLoader>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const submitBlockStyle = css`
  padding: ${space.atom} 0 ${space.atom2x};
  ${moreThanBreakpoint("desktop")} {
    padding-top: ${space.atom2x};
    width: 50%;
    margin-left: auto;
    margin-right: 0;
  }
`;
const blockStyle = css`
  margin: 0 auto;
  padding: ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    max-width: ${maxWidth.page1colWide};
    margin: ${space.atom} auto ${space.atom2x};
    box-sizing: border-box;
  }
  ${moreThanBreakpoint("desktop")} {
    padding: 0 ${space.atom};
  }
`;
const titleStyle = css`
  padding: ${space.atom2x} ${space.atom} ${space.atom};
  letter-spacing: 0.1em;
  font-size: ${fontSize.headline};
  color: ${textColor.primaryDarkColor};
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.title};
  }
`;
const flightTypeBlockStyle = css`
  padding-top: ${space.atom2x};
`;
const flightClassBlockStyle = css`
  width: 50%;
  min-width: 200px;
`;
